import { putRequest } from 'apis/fetcher'
import { VALID_NUMBER, VALID_YOUTUBE_EMBED_LINK } from 'infra/constants'
import { makeAutoObservable, reaction } from 'mobx'
import { mutate } from 'swr'
import {
  DashboardStartupDetail,
  StartupMember,
  StartupUser,
  ServiceArea,
} from 'types/common'
import { customToast, requestToast } from 'components/common/toast'
import { noticePrivateInfoDetected } from 'utils/handlers'
import { putRequestDashboardStartup } from 'apis/request'
import { checkEditorDefaultValue } from 'utils/validations'

const initialMember: StartupMember = {
  name: '',
  intro: '',
  career: '',
  role: '',
}

const initialFunding: {
  round: number
  amount: number
  attractedAt: string
} = {
  round: 0,
  amount: 0,
  attractedAt: '',
}

export class DashboardStartupStore {
  startup: DashboardStartupDetail
  user: StartupUser
  tagInput: string = ''
  hasChanged: boolean = false
  isOpenTossModal: boolean = false

  constructor(startup: DashboardStartupDetail, user: StartupUser) {
    this.startup = startup
    if (!startup.members.length) this.startup.members = [initialMember]
    this.user = user
    reaction(
      () => [
        this.startup.pt,
        this.startup.pitch,
        this.startup.thumbnail,
        this.startup.name,
        this.startup.briefIntro,
        this.startup.serviceAreas.map((item) => item.id),
        this.startup.tags,
        this.startup.funding.map((f) => [f.round, f.amount, f.attractedAt]),
        this.startup.effortForDev,
        this.startup.solvingProblem,
        this.startup.reasonForInvesting,
        this.startup.address,
        this.startup.location,
        this.startup.memberCount,
        this.startup.devCount,
        this.startup.hasCto,
        this.startup.compensation,
        this.startup.welfare,
        this.startup.images,
        this.startup.howToStartup,
        this.startup.whatKindCompany,
        this.startup.curOutcome,
        this.startup.vision,
        this.startup.lookForDev,
        this.startup.culture,
        this.startup.members,
      ],
      () => {
        this.hasChanged = true
      },
    )
    makeAutoObservable(this)
  }

  setIsOpenTossModal(v: boolean) {
    this.isOpenTossModal = v
  }

  get thumbnailUrl() {
    const { thumbnail } = this.startup
    if (!thumbnail) return ''
    if (typeof thumbnail === 'string') return thumbnail
    else return URL.createObjectURL(thumbnail)
  }

  get imageUrls() {
    return this.startup.images.map((image) => {
      if (typeof image !== 'string') return URL.createObjectURL(image)
      return image
    })
  }

  get serviceAreaIds() {
    return this.startup.serviceAreas.map((item) => item.id)
  }

  setHasChanged(v: boolean) {
    this.hasChanged = v
  }

  setStartup(startup: DashboardStartupDetail) {
    this.startup = startup
  }

  setMemberName(name: string, index: number) {
    this.startup.members[index].name = name
  }

  setMemberRole(role: string, index: number) {
    this.startup.members[index].role = role
  }

  setMemberCareer(career: string, index: number) {
    this.startup.members[index].career = career
  }

  setMemberIntro(intro: string, index: number) {
    this.startup.members[index].intro = intro
  }

  removeMember(index: number) {
    this.startup.members = this.startup.members.filter((_, i) => index !== i)
  }

  addMember() {
    this.startup.members = [...this.startup.members, initialMember]
  }

  setCulture(v: string) {
    this.startup.culture = v
  }

  setLookForDev(v: string) {
    this.startup.lookForDev = v
  }

  setVision(v: string) {
    this.startup.vision = v
  }

  setCurOutcome(v: string) {
    this.startup.curOutcome = v
  }

  setWhatKindCompany(v: string) {
    this.startup.whatKindCompany = v
  }

  setHowToStartup(v: string) {
    this.startup.howToStartup = v
  }

  addImage(file: File) {
    this.startup.images.forEach((image) => {
      if (
        image === file.name ||
        (typeof image !== 'string' && image.name === file.name)
      )
        throw '중복된 이미지입니다.'
    })
    this.startup.images = [...this.startup.images, file]
  }

  removeImage(index: number) {
    this.startup.images = this.startup.images.filter((_, i) => i !== index)
  }

  moveImage(from: number, to: number) {
    const images = [...this.startup.images]
    const [moved] = images.splice(from, 1)
    images.splice(to, 0, moved)
    this.startup.images = images
  }

  setWelfare(v: string) {
    this.startup.welfare = v
  }

  setCompensation(v: string) {
    this.startup.compensation = v
  }

  setCto(bool: boolean) {
    this.startup.hasCto = bool
  }

  setMemberCount(count: number) {
    this.startup.memberCount = count
  }

  setDevCount(count: number) {
    this.startup.devCount = count
  }

  setLocation(id: number | null) {
    this.startup.location = id
  }

  setAddress(v: string) {
    this.startup.address = v
  }

  setReasonForInvesting(v: string) {
    this.startup.reasonForInvesting = v
  }

  setSolvingProblem(v: string) {
    this.startup.solvingProblem = v
  }

  setEffortForDev(v: string) {
    this.startup.effortForDev = v
  }

  addFunding() {
    this.startup.funding = [...this.startup.funding, initialFunding]
  }

  removeFunding(index: number) {
    this.startup.funding = this.startup.funding.filter((_, i) => index !== i)
  }

  setFundingAttractedAt(v: string, index: number) {
    this.startup.funding[index].attractedAt = v
  }

  setFundingAmount(v: number, index: number) {
    if (!VALID_NUMBER.test(String(v))) return
    this.startup.funding[index].amount = v
  }

  setRound(v: number, index: number) {
    this.startup.funding[index].round = v
  }

  toggleServiceArea(item: ServiceArea) {
    if (this.serviceAreaIds.includes(item.id)) {
      this.startup.serviceAreas = this.startup.serviceAreas.filter(
        (_item) => _item.id !== item.id,
      )
    } else {
      this.startup.serviceAreas = [...this.startup.serviceAreas, item]
    }
  }

  addTagInput() {
    if (!this.tagInput.trim().length) return
    if (this.startup.tags.length >= 10)
      return alert('해시태그는 최대 10개까지 가능해요.')
    if (this.startup.tags.includes(this.tagInput)) return
    this.addTag(this.tagInput)
    this.tagInput = ''
  }

  addTag(v: string) {
    this.startup.tags = [...this.startup.tags, v]
  }

  removeTag(v: string) {
    this.startup.tags = this.startup.tags.filter((item) => item !== v)
  }

  setTagInput(v: string) {
    this.tagInput = v
  }

  setName(v: string) {
    this.startup.name = v
  }

  setBriefIntro(v: string) {
    this.startup.briefIntro = v
  }

  setPt(v: string) {
    this.startup.pt = v
  }

  setPitch(v: string) {
    this.startup.pitch = v
  }

  setThumbnail(file: File) {
    this.startup.thumbnail = file
  }

  checkValidity() {
    if (this.startup.pt && !VALID_YOUTUBE_EMBED_LINK.test(this.startup.pt)) {
      throw '기업 소개 영상은 유튜브 공유 링크만 입력해주세요.'
    }
    if (
      this.startup.pitch &&
      !VALID_YOUTUBE_EMBED_LINK.test(this.startup.pitch)
    ) {
      throw '채용설명회 영상은 유튜브 공유 링크만 입력해주세요.'
    }
    if (!this.startup.thumbnail) {
      throw '썸네일 이미지를 업로드해주세요.'
    }
    if (!this.startup.name) {
      throw '기업 이름을 입력해주세요.'
    }
    if (!this.startup.briefIntro) {
      throw '기업 한줄 소개를 입력해주세요.'
    }
    if (
      !this.startup.solvingProblem ||
      checkEditorDefaultValue(this.startup.solvingProblem)
    ) {
      throw '해결하는 문제를 입력해주세요.'
    }
    if (!this.startup.address) {
      throw '근무 위치를 입력해주세요.'
    }
    if (!this.startup.location) {
      throw '위치 분류를 입력해주세요.'
    }
    if (this.startup.memberCount === 0) {
      throw '인원 수를 입력해주세요.'
    }
    if (!this.serviceAreaIds.length) {
      throw '서비스 분야를 선택해주세요.'
    }
    if (!this.startup.images.length) {
      throw '소개 이미지를 하나 이상 업로드해주세요.'
    }
    if (
      !this.startup.howToStartup ||
      checkEditorDefaultValue(this.startup.howToStartup)
    ) {
      throw '어떻게 창업하셨나요?를 입력해주세요.'
    }
    if (
      !this.startup.whatKindCompany ||
      checkEditorDefaultValue(this.startup.whatKindCompany)
    ) {
      throw '어떤 회사인가요?를 입력해주세요.'
    }
    if (
      !this.startup.lookForDev ||
      checkEditorDefaultValue(this.startup.lookForDev)
    ) {
      throw '어떤 분을 찾고계신가요?를 입력해주세요.'
    }
    this.startup.members.forEach((member) => {
      if (!member.name || !member.role || !member.intro)
        throw '핵심 멤버의 필수 입력 정보(이름, 직책, 소개)를 입력해주세요.'
    })
  }

  async updateStartup() {
    return mutate('/dashboard/startup').then(({ data }) => {
      this.setStartup(data)
      this.hasChanged = false // setStartup 후 실행되야함
    })
  }

  async requestSave() {
    if (this.startup.isPublished && !window.confirm('저장하시겠습니까?')) return
    try {
      if (this.startup.isPublished) this.checkValidity()
      const res = await requestToast(this.save(), {
        loading: '저장중',
        success: `${
          this.startup.isPublished ? '저장' : '임시 저장'
        }되었습니다.`,
      })
      noticePrivateInfoDetected(res.data, '기업 상세 페이지')
      await this.updateStartup()
    } catch (e) {
      alert(e)
    }
  }

  async requestSaveAndPublish() {
    try {
      this.checkValidity()
      const res = await this.save()
      const hasDetected = noticePrivateInfoDetected(
        res.data,
        '기업 상세 페이지',
      )
      if (hasDetected) {
        this.updateStartup()
        customToast('삭제된 내용을 검토하신 후 다시 공개해주세요 :)')
        return
      }
      await requestToast(
        putRequest('/dashboard/startup/is-published', { value: true }),
        {
          loading: '요청중',
          success: '기업 정보가 공개되었습니다',
        },
      )
      await mutate('/dashboard/accounts/me').then(({ data }) =>
        this.setUser(data),
      )
    } catch (e) {
      alert(e)
    }
  }

  setUser(user: StartupUser) {
    this.user = user
  }

  async save() {
    const formData = new FormData()
    formData.append('name', this.startup.name ?? '')
    formData.append('pitch', this.startup.pitch ?? '')
    formData.append('pt', this.startup.pt ?? '')
    formData.append('thumbnail', this.startup.thumbnail ?? '')
    formData.append('briefIntro', this.startup.briefIntro ?? '')
    formData.append('reasonForInvesting', this.startup.reasonForInvesting ?? '')
    formData.append('effortForDev', this.startup.effortForDev ?? '')
    formData.append('solvingProblem', this.startup.solvingProblem ?? '')
    formData.append('address', this.startup.address ?? '')
    formData.append('location', String(this.startup.location) ?? '')
    formData.append('memberCount', String(this.startup.memberCount) ?? '')
    formData.append('devCount', String(this.startup.devCount) ?? '')
    formData.append('compensation', this.startup.compensation ?? '')
    formData.append('welfare', this.startup.welfare ?? '')
    formData.append('howToStartup', this.startup.howToStartup ?? '')
    formData.append('whatKindCompany', this.startup.whatKindCompany ?? '')
    formData.append('curOutcome', this.startup.curOutcome ?? '')
    formData.append('vision', this.startup.vision ?? '')
    formData.append('lookForDev', this.startup.lookForDev ?? '')
    formData.append('culture', this.startup.culture ?? '')
    formData.append('hasCto', String(this.startup.hasCto) ?? '')
    this.startup.serviceAreas.forEach((item) => {
      formData.append(`serviceAreas[]`, String(item.id))
    })
    this.startup.tags.forEach((item) => {
      formData.append(`tags[]`, item)
    })
    this.startup.images.forEach((item, index) => {
      formData.append(`images[${index}]`, item)
    })
    this.startup.members.forEach((item) => {
      formData.append(`members[]`, JSON.stringify(item))
    })
    this.startup.funding.forEach((item) => {
      if (!item.round && !item.amount && !item.attractedAt) return
      item.round = item.round ?? 0
      item.attractedAt = item.attractedAt ?? ''
      formData.append(`fundings[]`, JSON.stringify(item))
    })
    return await putRequestDashboardStartup(formData)
  }
}
